import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { LocationService } from 'src/app/services/location.service';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-location-detail',
  templateUrl: './location-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    '.col-lg-8.col-md-7 { font-weight: 700;  }'
  ]
})
export class LocationDetailComponent implements OnInit {
  addressOrLocation: string = "Location";
  canEdit = AppPermissions.canEditLocation;
  deleteHistory: ServerModels.ENESS_DeleteHistory;
  deleteHistoryTargetId: string;
  deleteHistoryTargetTableName: string = "ENESS_Address";
  personDetailAddress: boolean = false;
  showDeleteButton: boolean = false;
  showEditButton: boolean = false;
  showDeleteHistoryForm: boolean = false;

  @Output() deleteLocationCompleted: EventEmitter<string> = new EventEmitter<string>();
  @Output() editLocationLocationClicked: EventEmitter<string> = new EventEmitter<string>();

  private _addressDTO: ServerModels.ENESS_AddressDTO;
  @Input() set addressDTO(address: ServerModels.ENESS_AddressDTO) {
    this._addressDTO = address;
  }
  get addressDTO(): ServerModels.ENESS_AddressDTO {
    return this._addressDTO;
  }

  private _hasDeletePermission = false;
  @Input() set hasDeletePermission(hasDeletePermission: boolean) {
    this._hasDeletePermission = hasDeletePermission;
    this.showDeleteButton = this._hasDeletePermission;
  }

  @Input() set hasEditPermission(hasEditPermission: boolean) {
    this.showEditButton = hasEditPermission;
  }

  @Input() personAddressType: string;
  @Input() set isFromPersonDetails(isFromPersonDetails: boolean) {
    this.personDetailAddress = isFromPersonDetails;
    this.addressOrLocation = "Address";
  }

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(public locationService: LocationService,
    public elementRef: ElementRef,
    private messageService: MessageService,
    private route: ActivatedRoute) { }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngOnInit() {
    if (this.route.snapshot.routeConfig.component.name === 'LocationDetailComponent') {
      let addressId = this.route.snapshot.paramMap.get('id');
      if (!!addressId) {
        forkJoin({
          address:this.locationService.getAddressDetailsById(addressId),
          people:this.locationService.getAddressPeople(addressId),
          cases:this.locationService.getAddressLECases(addressId)
        }).pipe(takeUntil(this._unsubscribe$)).subscribe(
          (val: { address: ServerModels.ENESS_AddressDTO, people: ServerModels.AddressPeople[], cases: ServerModels.AddressLECases[] }) => {
              this._addressDTO = val.address;
              this.locationService.addressPeopleSubj.next(val.people);
              this.locationService.addressLECasesSubj.next(val.cases);
              
        },
            (err:any) => this.messageService.add({ key: 'locationDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message })
        );
      }
    }
  }

  public deleteLocation(): void {
    if (!this._hasDeletePermission || !this.addressDTO?.AddressID)
      return;

    this.deleteHistoryTargetId = this.addressDTO.AddressID;
    this.showDeleteHistoryForm = true;
  }

  public consolidatedAddressFormatter(address: string): string {
    if (address === undefined || address === null) return address;
    return address.replace(',', ', ');
  }

  public editLocation(): void {
    if (!this.showEditButton || (!this.addressDTO.AddressID && !this.addressDTO.AddressStagingID))
      return;

    this.editLocationLocationClicked.emit('Edit');
  }

  public onDeleteHistorySubmitted(deleteHistory: ServerModels.ENESS_DeleteHistory): void {
    if (deleteHistory != null) {
      this.showDeleteHistoryForm = false;
      this.locationService.deleteAddress(deleteHistory).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (location: ServerModels.ENESS_Address) => {
          this.deleteLocationCompleted.emit(deleteHistory.TargetID);
        }
      })
    }
  }

  public openRelatedCrimeGun(evt: Event, cgn: string): void {
    this.locationService.getCrimeGunId(cgn.trim()).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (id:string) => {
        evt.preventDefault();
        const token = sessionStorage.getItem(environment.authTokenStorageKey);
        let url = (<HTMLLinkElement>evt.target).baseURI + '/crimegun/' + id;
        let windowObjRef = window.open(url, '_blank');
        windowObjRef.sessionStorage.setItem(environment.authTokenStorageKey, token);
      }
    });
  }
}
